import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { RequestMethod } from 'app/shared/enum/request.method';
import { Subscription, throwError as observableThrowError } from 'rxjs';

// Environment
import { EnvironmentService } from 'app/shared/service/environments-service/environments-service';

// Findomestic
import { FndNotificationsService, Utils } from "findomestic-ui-components";

import { ENDPOINTS_MAP } from './endpoints-map';
import { catchError } from 'rxjs/operators';

export class ResponseError {
  code: number;
  message: string;
  longMessage: string;
  friendlyMessage: string;
  url: string;
  body?: any
}

@Injectable()
export class ApiService {

  environment: any;
  envsubscription: Subscription;
  baseUrl: string;

  // UUID
  securityHeaderGediSession: string = null;

  constructor(
    private http: HttpClient,
    public $e: EnvironmentService,
    public notificationService: FndNotificationsService
  ) {
    this.envsubscription = this.$e.get().subscribe(
      (data) => {
        if(data) {
          this.environment = data;
          this.baseUrl = this.environment.apiUrl;
        }
      }
    );
  }

  get(url: string, queryParams?: any, pathParams?: string[]) {
    return this.request(url, RequestMethod.GET, null, queryParams, pathParams);
  }

  post(url: string, body: Object, queryParams?: any, pathParams?: string[]) {
    return this.request(url, RequestMethod.POST, body, queryParams, pathParams);
  }

  put(url: string, body: Object, queryParams?: any, pathParams?: string[]) {
    return this.request(url, RequestMethod.PUT, body, queryParams, pathParams);
  }

  patch(url: string, body: Object, queryParams?: any, pathParams?: string[]) {
    return this.request(url, RequestMethod.PATCH, body, queryParams, pathParams);
  }

  delete(url: string, body: Object, queryParams?: any, pathParams?: string[]) {
    return this.request(url, RequestMethod.DELETE, body, queryParams, pathParams);
  }

  // general request
  request(url: string, method: RequestMethod, body?: Object, queryParams?: any, pathParams?: any[]) {
    let headers: HttpHeaders = this.headersManager(method);

    let finalUrl = this.getCurrentEndPoint(url, pathParams || []);

    // Workaround per poter utilizzare i mock anche con chiamate diverse da Get
    if(this.$e.selectedEnvironment.name==='local' || this.isMock(finalUrl)) {
      method = RequestMethod.GET;
    }

    switch(method) {
      case RequestMethod.GET: {
        return this.http.get(finalUrl, {
          headers: headers,
          params: queryParams ? queryParams : null
        }).pipe(
          catchError((res: any) => this.onRequestError(res))
        );
      }
      case RequestMethod.POST: {
        return this.http.post(finalUrl, body ? body : null, {
          headers: headers,
          params: queryParams ? queryParams : null
        }).pipe(
          catchError((res: any) => this.onRequestError(res))
        );
      }
      case RequestMethod.PUT: {
        return this.http.put(finalUrl, body ? body : null, {
          headers: headers,
          params: queryParams ? queryParams : null
        }).pipe(
          catchError((res: any) => this.onRequestError(res))
        );
      }
      case RequestMethod.PATCH: {
        return this.http.patch(finalUrl, body ? body : null, {
          headers: headers,
          params: queryParams ? queryParams : null
        }).pipe(
          catchError((res: any) => this.onRequestError(res))
        );
      }
      case RequestMethod.DELETE: {
        return this.http.delete(finalUrl, {
          headers: headers,
          params: queryParams ? queryParams : null
        }).pipe(
          catchError((res: any) => this.onRequestError(res))
        );
      }
    }
  }

  onRequestError(res: Response | any) {
    let errMsg: string;
    if(res instanceof Response) {
      const err = res || '';
      errMsg = `${res.status} - ${res.statusText || ''} ${err}`;
    }
    else {
      errMsg = res.message ? res.message : res.toString();
    }
    let dataError = new ResponseError();
    dataError.code = res.status;
    dataError.message = res.statusText;
    dataError.longMessage = errMsg;
    dataError.url = res.url;
    dataError.body = res || {};
    dataError.friendlyMessage = 'An error occurred retrieving data.';
    return observableThrowError(dataError);
  }

  private getCurrentEndPoint(url: string, pathParams: string[]): string {
    let endpoint: string;

    // check for current service in endpoints-map.ts
    if (!ENDPOINTS_MAP[url]) {
      endpoint = url;
    }
    // check if the service is mapped for the current environment in endpoints-map.ts
    else if((!ENDPOINTS_MAP[url][this.$e.selectedEnvironment.name])) {
      return url;
    }

    else endpoint = `${this.baseUrl}${ENDPOINTS_MAP[url][this.$e.selectedEnvironment.name]}`;

    let output;

    if(this.$e.selectedEnvironment.name === 'local' || this.isMock(endpoint)) {
      output = endpoint;
    }
    else {
      // return endpoint plus pathParams (if present)
      output = pathParams.reduce((path, param) => `${path}\/${param}`, endpoint);
    }

    return output;
  }

  private headersManager(requestMethod: RequestMethod): HttpHeaders {
    let headers = new HttpHeaders();

    if(requestMethod == RequestMethod.GET) {
      //get-requests only headers in order to avoid browser caching
      // headers = headers.append('Content-type', 'application/x-www-form-urlencoded; charset=UTF-8');
      headers = headers.append('Cache-control', 'no-cache');
      headers = headers.append('Cache-control', 'no-store');
      headers = headers.append('Expires', '0');
      headers = headers.append('Pragma', 'no-cache');
      headers = headers.append("x-technicaldatacall-requestid", Utils.createGUID());
      headers = headers.append("x-technicalDatacall-CallerReference", 'BD-HB');
      headers = headers.append("Apikey", 'rGsCAjNEmJmc7ahcqqPX2GfjGdsQbRi1');
    }
    //common headers
    // headers = headers.append('FrontEnd-Identifier', Utils.createGUID());
    // headers = headers.append('FrontEnd-Caller', 'BD-HB');

    // Fix Gedi-Session trackingData
    if(this.securityHeaderGediSession) {
      headers = headers.append('X-gedisession', this.securityHeaderGediSession);
    }

    return headers;
  }

  public static getUrlParameter(name, url) {
    if(!url) url = window.location.href;
    name = name.replace(/[\[\]]/g, "\\$&");
    let regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)")
    let results = regex.exec(url);
    if(!results) return '';
    if(!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, " "));
  }

  private isMock(endpoint) {
    return endpoint.includes('assets');
  }

}